<template>
  <div>
        <v-alert v-if="error" type="error" variant="tonal" density="compact" class="mb-3">{{ error }}</v-alert>

        <v-alert v-if="unpriced.length" type="warning" variant="tonal" density="compact" class="mb-3">
          Нет цены — расход не считается:
          <span v-for="(m, i) in unpriced" :key="m.provider + m.model">
            {{ i ? ', ' : '' }}<a href="#" @click.prevent="fillFrom(m)">{{ m.provider }} / {{ m.model }}</a> ({{ m.calls }} вызовов)
          </span>
        </v-alert>

        <div class="text-caption text-disabled mb-3">
          Цены в долларах за миллион токенов: «вне пика / в пик». Входные из кэша провайдера обычно
          намного дешевле. Цена действует с указанной даты до следующей записи; уже посчитанные
          расходы не пересчитываются. Нажмите на строку, чтобы взять её за основу новой цены.
        </div>

        <v-table density="compact" class="mb-4">
          <thead>
            <tr>
              <th>Поставщик</th>
              <th>Модель</th>
              <th class="text-right">Вход из кэша, $</th>
              <th class="text-right">Вход мимо кэша, $</th>
              <th class="text-right">Выход, $</th>
              <th>Пик (UTC)</th>
              <th>Действует с</th>
              <th>Сейчас</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="price in prices" :key="price.id" class="cursor-pointer" @click="fillFrom(price)">
              <td>{{ price.provider }}</td>
              <td>{{ price.model }}</td>
              <td class="text-right">{{ pair(price.input_cache_hit_price_usd ?? price.input_price_usd, price.peak_input_cache_hit_price_usd) }}</td>
              <td class="text-right">{{ pair(price.input_price_usd, price.peak_input_price_usd) }}</td>
              <td class="text-right">{{ pair(price.output_price_usd, price.peak_output_price_usd) }}</td>
              <td>{{ schedule(price) }}</td>
              <td>{{ formatDay(price.valid_from) }}</td>
              <td>
                <v-chip v-if="isCurrent(price) && price.peak_hours" size="x-small" :color="price.is_peak_now ? 'orange' : 'green'" variant="tonal">
                  {{ price.is_peak_now ? 'пик' : 'вне пика' }}
                </v-chip>
              </td>
            </tr>
            <tr v-if="!prices.length && !isLoading">
              <td colspan="8" class="text-center text-disabled">Цен нет</td>
            </tr>
          </tbody>
        </v-table>

        <div class="text-subtitle-2 mb-2">Новая цена</div>
        <v-row dense>
          <v-col cols="6" md="3">
            <v-text-field v-model="form.provider" label="Поставщик" variant="outlined" density="compact" hide-details></v-text-field>
          </v-col>
          <v-col cols="6" md="5">
            <v-text-field v-model="form.model" label="Модель (как в ответе провайдера)" variant="outlined" density="compact" hide-details></v-text-field>
          </v-col>
          <v-col cols="12" md="4">
            <v-text-field v-model="form.valid_from" label="Действует с" type="date" variant="outlined" density="compact" hide-details></v-text-field>
          </v-col>
        </v-row>
        <v-row dense>
          <v-col cols="12" class="text-caption text-medium-emphasis pt-2">Вне пика (обязательно, кроме входа из кэша)</v-col>
          <v-col v-for="f in offpeakFields" :key="f.key" cols="12" md="4">
            <v-text-field v-model.number="form[f.key]" :label="f.label" type="number" min="0" step="0.001" variant="outlined" density="compact" hide-details></v-text-field>
          </v-col>
          <v-col cols="12" class="text-caption text-medium-emphasis pt-2">В пик (пусто — как вне пика)</v-col>
          <v-col v-for="f in peakFields" :key="f.key" cols="12" md="4">
            <v-text-field v-model.number="form[f.key]" :label="f.label" type="number" min="0" step="0.001" variant="outlined" density="compact" hide-details clearable></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="form.peak_hours" label="Часы пика, UTC" placeholder="01-04,06-10" variant="outlined" density="compact" hide-details clearable></v-text-field>
          </v-col>
          <v-col cols="12" md="3">
            <v-text-field v-model="form.peak_weekdays" label="Дни пика" placeholder="1-5 (пн–пт)" variant="outlined" density="compact" hide-details clearable></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field v-model="form.offpeak_dates" label="Даты без пика (праздники)" placeholder="2026-10-01, 2026-10-02" variant="outlined" density="compact" hide-details clearable></v-text-field>
          </v-col>
        </v-row>
        <div class="d-flex justify-end mt-3">
          <v-btn color="primary" @click="savePrice" :loading="isSubmitting" :disabled="!isFormValid">Добавить цену</v-btn>
        </div>
  </div>
</template>

<script>
import apiClient from '@/apiClient'
import { formatMoney } from '@/utils/usageStats'

const PRICE_KEYS = ['input_cache_hit_price_usd', 'input_price_usd', 'output_price_usd',
  'peak_input_cache_hit_price_usd', 'peak_input_price_usd', 'peak_output_price_usd']

const emptyForm = () => ({
  provider: '',
  model: '',
  input_cache_hit_price_usd: null,
  input_price_usd: null,
  output_price_usd: null,
  peak_input_cache_hit_price_usd: null,
  peak_input_price_usd: null,
  peak_output_price_usd: null,
  peak_hours: '',
  peak_weekdays: '',
  offpeak_dates: '',
  valid_from: new Date().toISOString().slice(0, 10)
})

const filled = value => value !== null && value !== undefined && value !== ''

export default {
  name: 'LlmPricesPanel',
  emits: ['saved'],
  data() {
    return {
      prices: [],
      unpriced: [],
      form: emptyForm(),
      isLoading: false,
      isSubmitting: false,
      error: '',
      offpeakFields: [
        { key: 'input_cache_hit_price_usd', label: 'Вход из кэша, $' },
        { key: 'input_price_usd', label: 'Вход мимо кэша, $' },
        { key: 'output_price_usd', label: 'Выход, $' }
      ],
      peakFields: [
        { key: 'peak_input_cache_hit_price_usd', label: 'Вход из кэша в пик, $' },
        { key: 'peak_input_price_usd', label: 'Вход мимо кэша в пик, $' },
        { key: 'peak_output_price_usd', label: 'Выход в пик, $' }
      ]
    }
  },
  computed: {
    isFormValid() {
      const f = this.form
      const pricesOk = PRICE_KEYS.every(k => !filled(f[k]) || f[k] >= 0)
      return f.provider.trim() && f.model.trim() && f.valid_from && pricesOk &&
        filled(f.input_price_usd) && filled(f.output_price_usd)
    },
    // the newest price row of each model is the one in force (the list comes newest first)
    currentIds() {
      const seen = new Set()
      const ids = new Set()
      for (const p of this.prices) {
        const key = `${p.provider}/${p.model}`
        if (!seen.has(key)) {
          seen.add(key)
          ids.add(p.id)
        }
      }
      return ids
    }
  },
  mounted() {
    this.loadAll()
  },
  methods: {
    formatMoney,
    formatDay(value) {
      return new Date(value).toLocaleDateString('ru-RU')
    },
    pair(offpeak, peak) {
      const off = filled(offpeak) ? formatMoney(offpeak, 3) : '—'
      return filled(peak) ? `${off} / ${formatMoney(peak, 3)}` : off
    },
    schedule(price) {
      if (!price.peak_hours) return 'нет'
      return `${price.peak_hours}${price.peak_weekdays ? ', дни ' + price.peak_weekdays : ''}`
    },
    isCurrent(price) {
      return this.currentIds.has(price.id)
    },
    fillFrom(source) {
      const form = emptyForm()
      for (const key of Object.keys(form)) {
        if (key !== 'valid_from' && filled(source[key])) form[key] = source[key]
      }
      this.form = form
    },
    async loadAll() {
      this.isLoading = true
      this.error = ''
      try {
        const [prices, unpriced] = await Promise.all([
          apiClient.get('/admin/usage/prices'),
          apiClient.get('/admin/usage/prices/unpriced')
        ])
        this.prices = prices.data
        this.unpriced = unpriced.data
      } catch (error) {
        this.error = 'Ошибка загрузки цен: ' + (error.response?.data?.detail || error.message)
      } finally {
        this.isLoading = false
      }
    },
    async savePrice() {
      this.isSubmitting = true
      this.error = ''
      try {
        const payload = { ...this.form }
        for (const key of [...PRICE_KEYS, 'peak_hours', 'peak_weekdays', 'offpeak_dates']) {
          if (!filled(payload[key])) payload[key] = null
        }
        await apiClient.post('/admin/usage/prices', payload)
        this.form = emptyForm()
        await this.loadAll()
        this.$emit('saved')
      } catch (error) {
        const detail = error.response?.data?.detail
        const text = Array.isArray(detail) ? detail.map(d => d.msg).join('; ') : (detail || error.message)
        this.error = 'Ошибка сохранения цены: ' + text
      } finally {
        this.isSubmitting = false
      }
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
</style>
